// SPDX-License-Identifier: AGPL-3.0-or-later
// SPDX-FileCopyrightText: 2026 Dobrev IT Ltd

// Homeserver selection (Bitwarden-style). On the web the client is served by its
// homeserver, so it defaults to same-origin (""). In the native app (Capacitor
// WebView) there is no meaningful origin, so it defaults to the public Zalmoxis
// server, and the user may instead point it at a self-hosted URL. The choice is
// persisted; `Homeserver`/`SyncSocket` read it at request time.

const KEY = "zalmoxis.server";
export const PUBLIC_SERVER = "https://zalmoxis.chat";

// isNativeApp is true inside the Capacitor WebView shell.
export function isNativeApp(): boolean {
  const cap = (window as unknown as {Capacitor?: {isNativePlatform?: () => boolean}}).Capacitor;
  return !!cap?.isNativePlatform?.();
}

// isDesktopApp is true inside the bundled Electron shell (its preload exposes the flag). Like
// the native app, the UI is bundled and served locally (a custom app scheme), so there is no
// meaningful same-origin homeserver — the user picks one (cross-origin calls rely on the
// server's CORS).
export function isDesktopApp(): boolean {
  return !!(window as unknown as {zalmoxisDesktop?: unknown}).zalmoxisDesktop;
}

// isPackagedApp is true when the UI is bundled (native or desktop) rather than served by a
// homeserver — so "this server" (same-origin) is meaningless and a server must be chosen.
export function isPackagedApp(): boolean {
  return isNativeApp() || isDesktopApp();
}

function normalize(url: string): string {
  return url.trim().replace(/\/+$/, ""); // drop trailing slashes
}

// homeserverBase returns the API base URL — "" means same-origin.
export function homeserverBase(): string {
  const stored = localStorage.getItem(KEY);
  if (stored !== null) {
    return normalize(stored);
  }
  return isPackagedApp() ? PUBLIC_SERVER : "";
}

// setHomeserver persists the chosen base ("" for same-origin / "this server").
export function setHomeserver(base: string): void {
  localStorage.setItem(KEY, normalize(base));
}

// homeserverLabel describes the current selection for display.
export function homeserverLabel(): string {
  const base = homeserverBase();
  if (base === "") {
    return "this server";
  }
  if (base === PUBLIC_SERVER) {
    return "Zalmoxis";
  }
  return base.replace(/^https?:\/\//, "");
}
